import Button, { ButtonTypes } from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { t } from "i18next";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal, { CloseDialogEvent } from "molecules/modals/modal";
import React, { useEffect, useState } from "react";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import Heading from "atoms/typography/heading";
import PageRecord from "models/view-models/page-record";
import InputFormField from "molecules/form-fields/input-form-field";
import usePages from "utilities/hooks/domain/notebooks/use-pages";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { useHistory } from "react-router-dom";
import { ToastManager } from "utilities/toast/toast-manager";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import NotebookPageCoverImageUploader from "./notebook-page-cover-image-uploader";
import FileRecord from "models/view-models/file-record";

interface NotebookPageCreateModalProps {
    isVisible: boolean;
    closeDialog: (event?: CloseDialogEvent) => void;
    notebookId: number;
}

const BASE_CLASS_NAME = "c-create-modal";

const NotebookPageCreateModal = (props: NotebookPageCreateModalProps) => {
    const { isVisible, closeDialog, notebookId } = props;
    const [newPageTitle, setNewPageTitle] = useState<string>("");
    const [coverImage, setCoverImage] = useState<FileRecord | undefined>(
        undefined
    );
    const { createPage } = usePages();
    const { globalState } = useGlobalState();
    const currentUserId = globalState.currentIdentity?.userId();
    const [isValidPageName, setIsValidPageName] = useState<boolean>(true);
    const history = useHistory();

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );
    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;

    const { addPageToNotebook } = useNotebooksAtom();

    const onPageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewPageTitle(value);

        if (!isValidPageName && value.trim().length > 0) {
            setIsValidPageName(true);
        }
    };

    const createNewPageClick = async (title: string, notebookId: number) => {
        const isValid = title.trim().length > 0;
        setIsValidPageName(isValid);

        if (!isValid) return;

        try {
            const page = await createPage(
                new PageRecord({
                    ownerId: currentUserId,
                    name: title,
                    notebookId: Number(notebookId),
                    coverImageFileId: coverImage?.id ?? undefined,
                }),
                { notebookId: Number(notebookId) }
            );

            if (!page) {
                throw new Error("Page creation failed");
            }

            addPageToNotebook(notebookId, page);

            const route = RouteUtils.getUrl(
                siteMap.notebooks.notebook.newPage,
                {
                    notebookId: Number(notebookId),
                    pageId: page?.id,
                }
            );

            closeDialog();

            if (route) {
                history.push(route);
            }
        } catch (error) {
            ToastManager.error(t("notebooks-create-new-page__error"));
        }
    };

    const onClickCancel = () => {
        setIsValidPageName(true);
        setNewPageTitle("");
        closeDialog();
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        createNewPageClick(newPageTitle, notebookId);
    };
    const resetModal = () => {
        setCoverImage(undefined);
    };
    //TODO: Update this for edit page modal!
    useEffect(() => {
        resetModal();
    }, []);

    return (
        <Modal
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={closeDialog}
            cssClassName={BASE_CLASS_NAME}
            isVisible={isVisible}
            onCloseComplete={onClickCancel}
            label={t("notebooks-create-new-page")}
            type={modalType}>
            <Heading
                dataTestId="notebook-page-create-modal--heading"
                cssClassName={`${BASE_CLASS_NAME}__heading`}>
                {t("notebooks-create-new-page")}
                <Button
                    dataTestId="notebook-page-create-modal--corner-close-button"
                    accessibleText={t("close")}
                    cssClassName={"-modal-close -icon"}
                    onClick={closeDialog}
                    size={ButtonSizes.Medium}
                    style={ButtonStyles.TertiaryAlt}>
                    <Icon type={Icons.Close} size={IconSizes.Large} />
                </Button>
            </Heading>
            <form onSubmit={handleSubmit}>
                <div className={`${BASE_CLASS_NAME}__input`}>
                    <InputFormField
                        inputTestId="notebook-page-create-modal--title-input"
                        placeholder={t("notebooks-create-placeholder")}
                        onChange={onPageNameChange}
                        label={t("notebooks-create-label")}
                        value={newPageTitle}
                        maxLength={60}
                        isValid={isValidPageName}
                    />
                </div>
                <NotebookPageCoverImageUploader
                    coverImage={coverImage}
                    setCoverImage={setCoverImage}
                    currentUserId={currentUserId}
                />
                <div className={`${BASE_CLASS_NAME}__buttons`}>
                    <Button
                        dataTestId="notebook-page-create-modal--cancel-button"
                        type={ButtonTypes.Button}
                        accessibleText={t("cancel")}
                        onClick={onClickCancel}
                        style={ButtonStyles.Secondary}>
                        {t("cancel")}
                    </Button>
                    <Button
                        dataTestId="notebook-page-create-modal--create-button"
                        accessibleText={t("create")}
                        style={ButtonStyles.Primary}
                        cssClassName={`${BASE_CLASS_NAME}__create-button`}
                        type={ButtonTypes.Submit}>
                        <Icon type={Icons.Plus} />
                        {t("create")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default NotebookPageCreateModal;
